import './Tree.scss';
import { Ajax2, ITreeAjaxOptions } from '@shared/Ajax';
import Axios, { AxiosPromise } from 'axios';
import { Notify } from './Notify';
import { getFilenameWithVersion } from '@shared/utils/utils';
import { FileTreeService } from '@shared/services/FileTreeService';
import { IContextMenuItem, renderContextMenuAtPosition } from '@shared/ui-react/ContextMenu';
import { Events } from '@shared/Events';
import { CreateEditFolderDialog } from '@pages/files/dialogs/CreateEditFolderDialog';
import { LStorage } from '@shared/Storage';
import { makeObservable, observable } from 'mobx';
import { FileInfoDialog } from '@shared/files/ui/FileInfoDialog';
import { DateUtils } from '@shared/utils/DateUtils';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import * as React from 'react';
import { ITreeCustomizeOptions, TreeToolbar } from '@shared/ui-react/TreeToolbar';
import { alphaNumSort } from '@shared/utils/utils-sort';

export type CheckboxPropagateStrategy = false | 'first_level_files' | 'deep';

export interface ITreeRootOptions {
    showCheckboxes?: boolean;
    selectionCheckbox?: boolean; // selecting will trigger checkbox
    checkOnHighlightSelected?: boolean; // selecting highlighted item will also check it
    propagateCheckbox?: CheckboxPropagateStrategy; // when checking parent, check children
    showRoot?: boolean;
    rootEl?: JQuery; // where to append the whole tree, needed to trigger events
    childFilter?: (childData: ITreeData) => boolean; // predicate f to accept/reject children from ajax response
    renderView?: boolean; // should create html or just in memory model,
    secondaryIcon?: string; // small icon overlaid on top of main icon, eg to signal some node status
    matchesFilterFn?: (node: TreeNode, filterText: string) => boolean; // how node will be matches to user free-text filter,
    onContextMenu?: (node: TreeNode) => IContextMenuItem[];
    showContextMenu?: boolean;
    showContextMenuCreateFolder?: boolean;
    showContextMenuInfo?: boolean;
    hideNonPresentable?: boolean; // folders and files have presentable flag;
    storageKey?: string;
    storageIsGlobal?: boolean;
    expandOnStart?: TreeExpandOnStart;
    sortChildren?: boolean;

    hideEmptyFolders?: boolean;
    hideEmptyFoldersAlways?: boolean;
    fetchContentCountsFn?: () => Promise<any>; // number of items inside/assigned to tree node, printed next to name and used to 'hide empty'

    toolbar?: ITreeCustomizeOptions;
}

export interface ITreeOptions {
    id?: number;
    uuid?: string;
    headUuid?: string;
    text?: string;
    parent?: TreeNode;
    type?: TreeType;
    ext?: string; // extension for current node if file
}

type TreeStateOption = 'checked' | 'selected' | 'expanded' | 'highlighted' | 'enabled' | 'loading';

export type TreeEvent = 'fetch' | 'render' | 'select' | 'check' | 'error' | 'folder_created' | 'dblclick';

export type TreeExpandOnStart = 'none' | 'all' | 'firstLevel' | 'restoreState';

export enum TreeType {
    DIRECTORY,
    FILE,
    LINK,
}

interface ITreeState {
    checked: number; // 0 = false, 1 = true, 2 = indeterminate
    selected: boolean;
    expanded: boolean;
    highlighted: boolean;
    enabled: boolean;
    hidden: boolean; // hidden by 'hide empty'
    filtered: boolean; // hidden by text filter
    loading: boolean;
    readOnly: boolean;
}

const defaultState: ITreeState = {
    checked: 0,
    selected: false,
    expanded: false,
    highlighted: false,
    enabled: true,
    hidden: false,
    filtered: false,
    loading: false,
    readOnly: false,
};

export const childFilterPresets: {
    [key: string]: (childData: any) => boolean;
} = {
    everything: () => true,
    onlyFolders: (childData: ITreeData) => childData.hasOwnProperty('tolerance'), // only folder have .tolerance prop
    textDocuments: (c: any) => {
        if (c.tolerance) return true;
        return /nfcl|pdf|txt|doc|xls|docx|xlsx/.test(utils.getExtensionFromFilename(c.name));
    },
};

export class TreeNode {
    id: number = null;
    uuid: string = null;
    headUuid: string = null;
    text: string = null;
    type: TreeType = TreeType.DIRECTORY;
    ext: string = null;
    secondaryIcon: string = null;
    secondaryIconTooltip: string = null;
    alignmentNames?: string[];

    root: Tree = null; // will be set on init so we dont have to loop to it all the time
    parent: TreeNode = null;
    children: TreeNode[] = [];
    isRendered = false;

    ajaxData: ITreeData = null;

    state: ITreeState = {
        ...defaultState,
    };

    view: TreeView = null;

    axiosState: {
        promise: AxiosPromise;
        progress: number;
        error: string;
        isLoaded: () => boolean;
    } = {
        promise: null,
        progress: 0,
        error: null,
        isLoaded() {
            return this.progress === 100;
        },
    };

    constructor() {
        return this;
    }

    init(
        treeData: ITreeData,
        parent: TreeNode,
        opts?: {
            secondaryIcon?: string;
        }
    ) {
        this.ajaxData = treeData;

        this.parent = parent;
        let curr: { parent: any } = this;
        while (curr.parent != null) curr = curr.parent;
        this.root = curr as Tree;

        if (this.root?.renderView) {
            this.view = new TreeView(this);
        }

        if (treeData) {
            this.makeChildren(treeData);
        }

        if (treeData) {
            this.id = treeData.id;
            this.uuid = treeData.modelId ? 'link_' + treeData.id : treeData.uuid;
            if (!this.uuid) {
                this.uuid = String(this.id);
            }
            this.headUuid = treeData.headUuid;
            this.text = treeData.name;
        }
        this.secondaryIcon = opts?.secondaryIcon;

        // is file
        if (treeData?.isFile || (treeData?.hasOwnProperty('filesize') && !treeData?.hasOwnProperty('tolerance'))) {
            this.type = TreeType.FILE;
            this.ext = utils.getExtensionFromFilename(treeData.name);
            this.text = getFilenameWithVersion(treeData);
        }
        // is link
        else if (treeData?.hasOwnProperty('modelId')) {
            this.type = TreeType.LINK;
            this.text = treeData.modelName;
            this.ext = utils.getExtensionFromFilename(treeData.name);
        }

        const hasModelAndDrawingData = this.ajaxData.layerCounts?.features > 0 && this.ajaxData.layerCounts?.mapLayers > 0;
        if (!this.secondaryIcon && hasModelAndDrawingData) {
            this.secondaryIcon = 'iki iki-map-layers';
            this.secondaryIconTooltip = __('js_map.model_data_available');
        }

        // initial expanded state
        const expandOnStart = this.root.expandOnStart;
        if (expandOnStart === 'none') {
            this.state.expanded = this.isRoot();
        } else if (expandOnStart === 'firstLevel') {
            this.state.expanded = this.isRoot() || this.parent.isRoot();
        } else if (!expandOnStart || expandOnStart === 'all') {
            this.state.expanded = true;
        } else if (expandOnStart === 'restoreState' && this.isFolder()) {
            const expandedIds = this.root.expandedIdsStored;
            if (!expandedIds || (expandedIds?.size && expandedIds.has(this.id))) {
                this.state.expanded = true;
            }
        }

        return this;
    }

    private makeChildren(treeData: ITreeData) {
        // remove old children
        if (this.children.length > 0) {
            this.children.length = 0;
        }

        // collect folders, files, links into single array
        let jsonChildren = [...(treeData?.children ?? []), ...(treeData?.folders ?? []), ...(treeData?.links ?? []), ...(treeData?.models ?? [])].filter(c => c != null); // backend might mess up and send null child

        if (this.root?.sortChildren) {
            jsonChildren = jsonChildren.sort((a, b) => alphaNumSort(a.name, b.name));
        }

        // instantiate children
        const filterFn = this.root.childFilter;
        const hideNonPresentable = this.root.hideNonPresentable;

        for (const rawChild of jsonChildren) {
            const targetModel = rawChild.model /*filter links based on the model it links to*/ ?? rawChild;
            if (hideNonPresentable && 'presentable' in targetModel && !targetModel.presentable) {
                continue;
            }
            let child: TreeNode = null;
            if (filterFn) {
                // filter out some children with custom filter function
                if (filterFn(targetModel)) {
                    child = new TreeNode().init(rawChild, this);
                }
            } else {
                child = new TreeNode().init(rawChild, this);
            }
            if (child) {
                this.children.push(child);
                this.root.cachePut(child);
            }
        }
    }

    addChild(treeData: ITreeData): TreeNode {
        const child = new TreeNode().init(treeData, this);
        this.children.push(child);
        child.view?.render();
        this.setExpanded(true);
        return child;
    }

    // SET
    set(prop: TreeStateOption, newVal: any, invertOthers = false, forceChange = false, addToRecent = true): boolean {
        const old = this.state[prop];

        // only folder can be expanded
        if (prop === 'expanded' && !this.isFolder()) return false;

        // file can't be indeterminate
        if (prop === 'checked') {
            if (!this.isFolder()) {
                newVal = newVal > 0 ? 1 : 0;
            }
        }

        // do nothing if new value is the same as old
        if (old === newVal && !forceChange) return false;

        // add to recent list
        if (addToRecent && prop === 'selected' && newVal == true) {
            if (this.root) {
                this.root.recentPointerIndex = 0;
                this.root.recentlySelected = this.root.recentlySelected.slice(0, 19).filter((v, i, a) => a.findIndex(v2 => v2.uuid === v.uuid) === i);
                this.root.recentlySelected.unshift({ uuid: this.uuid, label: this.ajaxData.name });
            }
        }

        (this.state[prop] as any) = newVal;
        if (this.view) {
            this.view.renderState(prop);
        }

        // set other nodes to inverse of this node
        // eg. select me <> deselect all others, expand me <> collapse others
        if (invertOthers) {
            if (this.parent != null) {
                this.root.set(prop, !newVal, false, false);
            }
            this.root.each((child: TreeNode) => {
                if (child !== this) {
                    // skip me
                    child.set(prop, !newVal, false, false);
                }
            }, true);
        }

        return true;
    }

    setChildren(prop: TreeStateOption, newVal: any, includeSelf = true) {
        this.each(
            tree => {
                if (tree.get(prop) !== newVal) tree.set(prop, newVal);
            },
            true,
            includeSelf
        );
    }

    toggle(prop: TreeStateOption, invertOthers?, forceChange?): boolean {
        return this.set(prop, !this.get(prop), invertOthers, forceChange);
    }

    get(prop: TreeStateOption): any {
        return this.state[prop];
    }

    setExpanded(state = true, animate = false) {
        if (this.isRoot() || !this.isFolder()) return false;
        this.state.expanded = state;
        if (this.view) {
            this.view.renderExpanded(animate ? this.root.cacheSize() < 1000 : false);
        }
        this.root.persistState();
    }

    setChecked(state = 1, silent = false, propagate = this.root.propagateCheckbox, includeHidden = false) {
        let target: TreeNode = this;
        if (this.isLink()) {
            target = this.root.getByUuid(this.ajaxData.modelUuid);
            if (!target) {
                return;
            }
        }
        const changed: TreeNode[] = [];

        if (target.state.enabled && target.state.checked !== state) {
            changed.push(target);

            if (target.isFolder()) {
                this.state.checked = state;
            } else {
                // file can't be indeterminate
                target.state.checked = state > 0 ? 1 : 0;
            }

            if (target.view) {
                target.view.renderState('checked');
            }
        }

        // set the same state on children
        if (propagate) {
            target.each(child => {
                if (child.isFolder() && propagate === 'first_level_files') return;

                let cTarget = child;
                if (cTarget.state.enabled && !cTarget.state.filtered && (includeHidden || (!includeHidden && !cTarget.state.hidden))) {
                    if (cTarget.isLink()) {
                        child.set('checked', state);
                        cTarget = this.root.getByUuid(cTarget.ajaxData.modelUuid);
                    }
                    if (cTarget) {
                        const hasChanged = cTarget.set('checked', state);
                        if (hasChanged) changed.push(cTarget);
                    }
                }
            }, propagate === 'deep');
        }

        // ajax
        if (state === 0 && target.axiosState.promise) {
            target.axiosState.progress = 100;
            target.setLoading(false);
        }

        // trigger check event
        if (!silent && changed.length > 0) {
            this.root.trigger('check', {
                node: target,
                changed,
            });
        }

        // dont call 'target'.syncAllLinksWithTargets, it will run sync on every tree node and we need it called once just for root
        this.root.syncAllLinksWithTargets();
    }

    toggleCheckedAndForcePropagate() {
        const state = this.get('checked') ? 0 : 1;
        this.setChecked(state);

        if (!this.root.propagateCheckbox || this.root.propagateCheckbox === 'first_level_files') {
            this.setChecked(state, false, 'deep');
        }

        if (this.root.propagateCheckbox) {
            this.root.calculateCheckboxes();
        }
    }

    setEnabled(state: boolean) {
        this.set('enabled', state);
    }

    setHighlighted(state: boolean, animateExpand: boolean) {
        this.set('highlighted', state, state);
        this.parent?.setExpanded(true, animateExpand);
        if (state) {
            this.scrollTo();
        }
    }

    checkIds(ids: number[] = [], clearExisting = true, calculateCheckboxes = true, silent = false) {
        this.checkNodes(
            ids.map(id => this.getById(id)),
            clearExisting,
            calculateCheckboxes,
            silent
        );
    }

    checkUuids(uuids: string[] = [], clearExisting = true, calculateCheckboxes = true, silent: boolean) {
        this.checkNodes(
            uuids.map(uuid => this.getByUuid(uuid)),
            clearExisting,
            calculateCheckboxes,
            silent
        );
    }

    private checkNodes(nodes: TreeNode[], clearExisting = true, calculateCheckboxes = true, silent: boolean) {
        if (clearExisting) {
            this.getChecked().forEach(n => {
                if (nodes.includes(n)) return;
                n.setChecked(0, silent);
            });
        }

        nodes.forEach(node => {
            if (node) {
                node.setChecked(1, silent);
            }
        });

        if (calculateCheckboxes) {
            this.root.calculateCheckboxes();
        }
    }

    checkIdsOrHeadUuids(ids: number[], headUuids: string[] = [], clearExisting = true, calculateCheckboxes = true) {
        if (clearExisting) {
            this.getChecked().forEach(n => {
                if (ids.includes(n.ajaxData.id) || headUuids.includes(n.ajaxData.headUuid)) return;
                n.setChecked(0);
            });
        }

        for (const i in headUuids.length ? headUuids : ids) {
            if (headUuids.length && headUuids[i]) {
                const node = this.getByHeadUuid(headUuids[i]);
                if (node) {
                    node.setChecked(1);
                }
            } else if (ids.length && ids[i]) {
                const node = this.getById(ids[i]);
                if (node) {
                    node.setChecked(1);
                }
            }
        }

        if (calculateCheckboxes) {
            this.root.calculateCheckboxes();
        }
    }

    uncheckIds(ids: number[], checkAllOthers = false, calculateCheckboxes = true) {
        this.uncheckNodes(
            ids.map(uuid => this.getById(uuid)),
            checkAllOthers,
            calculateCheckboxes
        );
    }

    uncheckUuids(uuids: string[], checkAllOthers = false, calculateCheckboxes = true) {
        this.uncheckNodes(
            uuids.map(uuid => this.getByUuid(uuid)),
            checkAllOthers,
            calculateCheckboxes
        );
    }

    private uncheckNodes(nodes: TreeNode[], checkAllOthers = false, calculateCheckboxes = true) {
        if (checkAllOthers) {
            this.setChecked(1, false, 'deep');
        }

        nodes.forEach(node => {
            if (node) {
                node.setChecked(0);
            }
        });

        if (calculateCheckboxes) {
            this.root.calculateCheckboxes();
        }
    }

    expandIds = (ids: number[] = []) => {
        this.expandNodes(ids.map(id => this.getById(id)));
    };

    expandUuids = (uuids: string[] = []) => {
        this.expandNodes(uuids.map(uuid => this.getByUuid(uuid)));
    };

    private expandNodes(nodes: TreeNode[]) {
        nodes.forEach(node => {
            if (node) {
                node.eachParent(parent => parent.setExpanded(true));
                node.setExpanded(true);
            }
        });
    }

    expandIdsOrHeadUuids = (ids: number[] = [], uuids: string[] = []) => {
        for (const i in uuids) {
            if (uuids[i]) {
                const node = this.getByHeadUuid(uuids[i]);
                if (node) {
                    node.eachParent(parent => parent.setExpanded(true));
                    node.setExpanded(true);
                }
            } else if (ids[i]) {
                const node = this.getById(ids[i]);
                if (node) {
                    node.eachParent(parent => parent.setExpanded(true));
                    node.setExpanded(true);
                }
            }
        }
    };

    setLoading(state: boolean) {
        this.set('loading', state);
    }

    setText(text: string) {
        if (!text) return;
        this.text = text;
        if (this.view) {
            this.view.renderText();
        }
    }

    hasAnyVisibleChildren = (alwaysShowFiles = true): boolean => {
        if (this.isRoot()) return true; // root can't be empty
        if (this.isFileOrLink()) {
            // file or link can't be hidden with 'hide empty folders' but can with 'text filter'
            return alwaysShowFiles;
        }
        return !(this.isFolder() && (this.children.length === 0 || this.children.filter(child => !child.isHiddenOrFiltered()).length === 0));
    };

    setSelected(state = true, deselectOthers = true, silent = false) {
        let target: TreeNode = this;
        if (target.isLink()) {
            target = this.root.getByUuid(this.ajaxData.modelUuid);
            if (!target) {
                return;
            }
        }

        // prevent if disabled
        if (!target.state.enabled) return false;

        // expand parent folders, useful when selecting via code
        if (state === true) {
            target.eachParent(
                parent => {
                    parent.setExpanded(true);
                },
                true,
                false
            );

            // if selecting a folder, expand it
            if (target.isFolder()) {
                target.setExpanded(true);
            }
        }

        // if using checkboxes, selecting element will trigger checkbox
        if (this.root.showCheckboxes && this.root.selectionCheckbox && !target.isFolder()) {
            target.setChecked(target.get('checked') === 0 ? 1 : 0);

            if (this.root.propagateCheckbox) {
                this.root.calculateCheckboxes();
            }

            return true;
        }

        // save previous selection to send in event
        const prevSelection = target.root.where('selected', true, true, true);

        // set new selection state
        target.set('selected', state, deselectOthers);

        // trigger selection event
        if (!silent) {
            this.root.trigger('select', {
                node: target,
                previous: prevSelection,
            });
        }
    }

    selectByUuid(uuid: string, fallbackToRoot = true): TreeNode {
        let target = this.getByUuid(uuid);
        if (uuid === null && target) {
            target = null;
        }
        return this.selectTargetNode(target, fallbackToRoot);
    }

    selectById(id: number, fallbackToRoot = true): TreeNode {
        let target = this.getById(id);
        if (id === null && target) {
            target = null;
        }
        return this.selectTargetNode(target, fallbackToRoot);
    }

    private selectTargetNode(target: TreeNode, fallbackToRoot = true): TreeNode {
        if (!target && fallbackToRoot && this.root.showRoot == true) {
            target = this.root;
        }

        if (target) {
            if (target.isRoot() && !this.root.showRoot) {
                target = target.children[0];
            }

            target.setSelected();
        } else {
            const selected = this.root.getSelected();
            if (selected) {
                selected.setSelected(false, false, true);
            }
        }

        return target;
    }

    scrollTo(): TreeNode {
        if (this.view) {
            this.view.scrollTo();
        }
        return this;
    }

    getById(id: number): TreeNode {
        return this.where('id', id, true, true)[0];
    }

    getByUuid(uuid: string): TreeNode {
        return this.where('uuid', uuid, true, true)[0];
    }

    getByHeadUuid(uuid: string): TreeNode {
        return this.where('headUuid', uuid, true, true)[0];
    }

    isRoot(): boolean {
        return this.parent === null && this.isFolder();
    }

    setSecondaryIcon = (icon: string, tooltip?: string): void => {
        this.secondaryIcon = icon;
        this.secondaryIconTooltip = tooltip;
        this.view?.renderSecondaryIcon();
    };

    setError = (error: string) => {
        this.axiosState.error = error;
        this.view?.renderError();
    };

    // GET

    isFolder(): boolean {
        return this.type === TreeType.DIRECTORY;
    }

    isFile(): boolean {
        return this.type === TreeType.FILE;
    }

    isLink(): boolean {
        return this.type === TreeType.LINK;
    }

    isFileOrLink(): boolean {
        return this.type === TreeType.FILE || this.type === TreeType.LINK;
    }

    isChecked(): boolean {
        return this.state.checked !== 0;
    }

    isHiddenOrFiltered(): boolean {
        return this.state.hidden || this.state.filtered;
    }

    hasChildren(): boolean {
        return this.children.length > 0;
    }

    getParents(includeRoot = false): TreeNode[] {
        const parents: TreeNode[] = [];

        // tslint:disable-next-line
        let curr: TreeNode = this;
        while (curr.parent != null) {
            curr = curr.parent;
            if (curr.isRoot() && !includeRoot) {
                break;
            }
            parents.push(curr);
        }

        return parents.reverse();
    }

    getSelected(): TreeNode {
        return this.where('selected', true, true, true)[0];
    }

    getChecked(): TreeNode[] {
        return this.where('checked', 1, true, true);
    }

    getCheckedAndUncheckedFolderIds() {
        return this.getCheckedAndUncheckedIds(TreeType.DIRECTORY);
    }

    getCheckedAndUncheckedFileIds() {
        return this.getCheckedAndUncheckedIds(TreeType.FILE);
    }

    getCheckedAndUncheckedIds(nodeType: TreeType): {
        checkedHeadUuids: string[];
        checkedIds: number[];
        checkedNames: string[];
        uncheckedIds: number[];
    } {
        const checkedHeadUuids: string[] = [];
        const checkedIds: number[] = [];
        const checkedNames: string[] = [];
        const uncheckedIds: number[] = [];

        this.each(
            c => {
                if (c.type === nodeType) {
                    if (c.state.checked == 1) {
                        checkedHeadUuids.push(c.headUuid);
                        checkedIds.push(c.id);
                        checkedNames.push(c.text);
                    } else {
                        uncheckedIds.push(c.id);
                    }
                }
            },
            true,
            true
        );

        return {
            checkedHeadUuids,
            checkedIds,
            checkedNames,
            uncheckedIds,
        };
    }

    /**
     * Get all (not just direct) children for this subtree
     * To get nodes from the whole tree, do .root first
     * Does not include root node
     */
    getChildren(deep = true, onlyVisible = false, depthFirst = false): TreeNode[] {
        if (!deep) {
            return this.children.filter(c => (onlyVisible ? !c.isHiddenOrFiltered() : true));
        }

        if (depthFirst) {
            return this.getChildrenDepthFirst(onlyVisible);
        }

        const allSubNodes: TreeNode[] = [];
        const traverse: TreeNode[] = [this];

        while (traverse.length) {
            const curr = traverse.shift();
            curr.children
                .filter(c => (onlyVisible ? !c.isHiddenOrFiltered() : true))
                .forEach(node => {
                    traverse.unshift(node);
                    allSubNodes.push(node);
                });
        }

        return allSubNodes;
    }

    getChildrenDepthFirst(onlyVisible = false): TreeNode[] {
        const result: TreeNode[] = [];
        const addNodesDepthFirst = (node: TreeNode) => {
            const children = node.children.filter(c => (onlyVisible ? !c.isHiddenOrFiltered() : true));
            children.forEach(child => {
                result.push(child);
                addNodesDepthFirst(child);
            });
        };

        addNodesDepthFirst(this);
        return result;
    }

    /**
     * Filter all SUBTREE nodes by param value (ex. checked=true)
     * To filter the whole tree do .root first
     */
    where(param: string, value: any = true, includeSelf = true, deep = false): TreeNode[] {
        // get just direct children or all children
        const nodes = this.getChildren(deep);
        if (includeSelf) nodes.unshift(this);

        return nodes.filter(child => child[param] == value || child.state[param] == value);
    }

    // ITERATE
    each(callback: (child: TreeNode) => void, deep = false, includeSelf = false): void {
        if (!this.isFolder() || !this.hasChildren()) return;

        const nodes = this.getChildren(deep);
        if (includeSelf) nodes.push(this);

        nodes.forEach((node: TreeNode) => {
            callback(node);
        });
    }

    eachParent(callback: (parent: TreeNode) => void, includeRoot = false, includeSelf = true): void {
        // tslint:disable-next-line
        let curr: TreeNode = this;
        if (includeRoot) {
            do {
                callback(curr);
                curr = curr.parent;
            } while (curr);
        } else {
            if (!includeSelf) {
                curr = curr.parent;
            }
            while (curr.parent != null) {
                callback(curr);
                curr = curr.parent;
            }
        }
    }

    eachPostOrder(node: TreeNode, callback: (node: TreeNode) => void) {
        for (const child of node.children) {
            this.eachPostOrder(child, callback);
        }
        callback(node);
    }

    loadWithAxios(axiosOptions: ITreeAjaxOptions) {
        // const fSize = axiosOptions.filesize;
        const fSize = 0;
        // const listL = axiosOptions.listLoadingModels;
        const listL = [];
        const modelId = axiosOptions.modelId;
        // this.root.set('loading', true);

        //this.setLoading(true);
        if (this.axiosState.promise) {
            this.axiosState.progress = 0;
        }

        const progress = document.getElementById('progress');
        const bar = document.getElementById('bar');

        if (progress) {
            progress.style.transition = 'opacity 0s';
        }

        if (bar) {
            bar.style.animation = 'shadow-pulse 1s infinite';
        }

        this.axiosState.promise = Axios.get(axiosOptions.url, {
            onDownloadProgress: progressEvent => {
                if (progress) {
                    progress.style.opacity = '1';
                }

                if (axiosOptions.layerCounts > 1) {
                    if (Math.round((progressEvent.loaded * 100) / 1.7 / fSize) <= 95) {
                        this.axiosState.progress = Math.round((progressEvent.loaded * 100) / 1.7 / fSize);
                    } else {
                        this.axiosState.progress = 95;
                    }
                } else {
                    if (axiosOptions.filesize > 3000000) {
                        if (Math.round((progressEvent.loaded * 100) / 1.7 / fSize) <= 95) {
                            this.axiosState.progress = Math.round((progressEvent.loaded * 100) / 1.7 / fSize);
                        } else {
                            this.axiosState.progress = 95;
                        }
                    } else {
                        if (Math.round((progressEvent.loaded * 100 * 2) / fSize) <= 95) {
                            this.axiosState.progress = Math.round((progressEvent.loaded * 100 * 2) / fSize);
                        } else {
                            this.axiosState.progress = 95;
                        }
                    }
                }

                if (progress) {
                    progress.style.width = this.axiosState.progress / listL.length + '%';
                }
            },
        }) as AxiosPromise;

        this.axiosState.promise
            .then(() => {
                this.axiosState.progress = 100;
                this.axiosState.error = null;
            })
            .catch(() => {
                this.axiosState.error = "Can't load";
                this.axiosState.progress = 0;
                this.setChecked(0, true, 'deep');
            })
            .then(() => {
                this.axiosState.promise = null;
                const index = listL.indexOf(modelId, 0);
                if (index > -1) {
                    listL.splice(index, 1);
                }
                if (listL[0] == 'sv') {
                    const uniqueListL = listL.filter((elem, index, self) => {
                        return index === self.indexOf(elem);
                    });

                    if (uniqueListL.length == 1) {
                        this.root.finishedLoading();
                        Notify.success('Saved view successfully loaded!');
                    } else if (uniqueListL.length == 2 && uniqueListL[1] == 'nn') {
                        this.root.finishedLoading();
                        Notify.warn('Saved view successfully loaded with missing models!');
                    }
                } else {
                    if (listL.length == 0) {
                        this.root.finishedLoading();
                    }
                }

                if (!axiosOptions.ignoreLoading) {
                    this.setLoading(false);
                }

                if (this.view) {
                    this.view.renderError();
                }
            });

        return this.axiosState.promise;
    }

    toggleExpandAll = () => {
        if (!this.children.length) return;

        if (this.children[0].state.expanded) {
            // root is always expanded, so decide based on first child
            this.collapseAll();
        } else {
            this.expandAll();
        }
    };

    expandAll = () => {
        this.each(tree => tree.setExpanded(true, false), true);
    };

    collapseAll = () => {
        this.each(tree => tree.setExpanded(false, false), true);
    };
}

export class Tree extends TreeNode {
    rootEl: JQuery;
    showRoot = false;
    propagateCheckbox: CheckboxPropagateStrategy = false;
    checkOnHighlightSelected = false;
    selectionCheckbox = false;
    showCheckboxes = false;
    storageKey: string = null;
    storageIsGlobal = false;
    renderView = true;

    showContextMenu = true;
    showContextMenuCreateFolder = false;
    showContextMenuInfo = false;

    toolbar: ITreeCustomizeOptions = {
        show: true,
    };

    hideNonPresentable = false;
    sortChildren = true;
    @observable expandOnStart: TreeExpandOnStart = 'all';
    expandedIdsStored: Set<number> = null;
    @observable hideEmptyFolders = false;
    @observable hideEmptyFoldersAlways = false;
    fetchContentCountsFn: () => Promise<any> = null;

    childFilter: (childData: any) => boolean = childFilterPresets.everything;
    parse: (rawJson: any) => any = null;
    matchesFilterFn = (node: TreeNode, filterText: string) => !node.text.toLowerCase().includes(filterText);
    onContextMenu: (node: TreeNode) => IContextMenuItem[] = null;

    @observable recentlySelected: Array<{ uuid: string; label: string }> = [];
    recentPointerIndex = 0; // for moving through recent list with back/forward

    private filterText = '';
    private expandedIdsBeforeFilter: number[] = [];

    hideEmptyFiles = false; // sometimes files can have counts (attached logpoints), hide them based on count
    contentCounts = {};

    @observable featuresLoadedMap = new Map<string, boolean>();

    private uuidCache: Map<string, TreeNode> = new Map();
    private idCache: Map<number, TreeNode> = new Map();

    @observable isLoading = false;

    constructor(opts: ITreeRootOptions) {
        super();
        makeObservable(this);

        this.root = this;
        this.rootEl = opts.rootEl ?? $(document.createElement('div')); // needed to trigger events
        this.state.expanded = true; // root should always be expanded on start

        if (opts.hasOwnProperty('showCheckboxes')) {
            this.showCheckboxes = opts.showCheckboxes;
        }
        if (opts.hasOwnProperty('selectionCheckbox')) {
            this.selectionCheckbox = opts.selectionCheckbox;
        }
        if (opts.hasOwnProperty('propagateCheckbox')) {
            this.propagateCheckbox = opts.propagateCheckbox;
        }
        if (opts.hasOwnProperty('checkOnHighlightSelected')) {
            this.checkOnHighlightSelected = opts.checkOnHighlightSelected;
        }
        if (opts.hasOwnProperty('showRoot')) {
            this.showRoot = opts.showRoot;
        }
        if (opts.hasOwnProperty('renderView')) {
            this.renderView = opts.renderView;
        }
        if (opts.hasOwnProperty('onContextMenu')) {
            this.onContextMenu = opts.onContextMenu;
        }
        if (opts.hasOwnProperty('showContextMenu')) {
            this.showContextMenu = opts.showContextMenu;
        }
        if (opts.hasOwnProperty('showContextMenuCreateFolder')) {
            this.showContextMenuCreateFolder = opts.showContextMenuCreateFolder;
        }
        if (opts.hasOwnProperty('showContextMenuInfo')) {
            this.showContextMenuInfo = opts.showContextMenuInfo;
        }
        if (opts.hasOwnProperty('hideNonPresentable')) {
            this.hideNonPresentable = opts.hideNonPresentable;
        }
        if (opts.hasOwnProperty('sortChildren')) {
            this.sortChildren = opts.sortChildren;
        }
        if (opts.hasOwnProperty('storageKey')) {
            this.storageKey = opts.storageKey;
        }
        if (opts.hasOwnProperty('storageIsGlobal')) {
            this.storageIsGlobal = opts.storageIsGlobal;
        }
        if (opts.hasOwnProperty('expandOnStart')) {
            this.expandOnStart = opts.expandOnStart;
        }
        if (opts.hasOwnProperty('hideEmptyFoldersAlways')) {
            this.hideEmptyFoldersAlways = opts.hideEmptyFoldersAlways;
        }
        if (opts.hasOwnProperty('toolbar')) {
            this.toolbar = opts.toolbar;
        }
        if (typeof opts.fetchContentCountsFn === 'function') {
            this.fetchContentCountsFn = opts.fetchContentCountsFn;
        }
        if (typeof opts.childFilter === 'function') {
            this.childFilter = opts.childFilter;
        }
        if (typeof opts.matchesFilterFn === 'function') {
            this.matchesFilterFn = opts.matchesFilterFn;
        }

        const storedState = this.getStoredState();
        if (storedState?.hasOwnProperty('expandOnStart')) {
            this.expandOnStart = storedState.expandOnStart;
        }
        if (Array.isArray(storedState?.expandedIds)) {
            this.expandedIdsStored = new Set(storedState?.expandedIds);
        }
        if (!this.hideEmptyFoldersAlways && storedState?.hasOwnProperty('hideEmptyFolders')) {
            this.hideEmptyFolders = storedState.hideEmptyFolders;
        }

        this.hideEmptyFolders = this.hideEmptyFoldersAlways ? true : !!opts.hideEmptyFolders;
    }

    destroy() {
        this.uuidCache.clear();
        this.idCache.clear();

        this.offAll();
        if (this.view) {
            this.view.destroy();
            this.view = null;
        }
    }

    async initAndRender(ajaxData: ITreeData, includesFiles = false) {
        if (!ajaxData) {
            console.warn('Cannot init file tree without data');
            return;
        }

        this.init(ajaxData, null);

        this.uuidCache.clear();
        this.idCache.clear();
        this.each((node: TreeNode) => {
            if (node.uuid) {
                this.uuidCache.set(node.uuid, node);
            }
            if (node.id) {
                this.idCache.set(node.id, node);
            }
        }, true);

        if (this.renderView) {
            this.view.render();
        }

        // if no function for counts is provided, and tree contains files, count the real files and use that count for 'hide empty'
        if (includesFiles && !this.fetchContentCountsFn) {
            this.fetchContentCountsFn = () => Promise.resolve(this.getRealFileCounts());
            this.hideEmptyFiles = true;
        }
        this.hideEmptyFiles = !includesFiles;

        if (typeof this.fetchContentCountsFn === 'function') {
            this.fetchAndRenderContentCounts().finally(() => {
                if (this.hideEmptyFolders) {
                    this.hideEmptyFoldersApply(false);
                }
                this.view?.render();

                this.trigger('render', {
                    model: this,
                });
            });
        } else {
            this.view?.render();

            this.trigger('render', {
                model: this,
            });
        }

        const clonedData = JSON.parse(JSON.stringify(ajaxData));
        this.trigger('fetch', clonedData);
        return this;
    }

    fetch(includeFiles = false): Promise<any> {
        let promise: Promise<ITreeData>;
        if (includeFiles) {
            promise = FileTreeService.fetchFileTree(false);
        } else {
            promise = FileTreeService.fetchFolderTree(false);
        }

        promise
            .then(data => {
                this.initAndRender(data, includeFiles);
            })
            .catch(data => {
                console.warn('FileTree: Fetching tree failed or aborted, data: ', data);
                this.trigger('error', data);
            });

        return promise;
    }

    public async fetchAndRenderContentCounts() {
        this.progressIndeterminateShow();

        const promise = this.fetchContentCountsFn();

        promise
            .then(counts => {
                if (counts != null) {
                    this.contentCounts = counts;
                    this.renderNodeCountsManual(counts, null);
                }
            })
            .finally(() => this.progressIndeterminateHide());

        return promise;
    }

    goBack = () => {
        this.recentPointerIndex = utils.clamp(0, this.recentPointerIndex + 1, this.recentlySelected.length);
        const recent = this.recentlySelected[this.recentPointerIndex];
        if (recent) {
            const target = this.getByUuid(recent.uuid);
            if (target) {
                target.set('selected', true, true, false, false);
                this.trigger('select', {
                    node: target,
                    previous: [],
                });
            }
        }
    };

    goForward = () => {
        this.recentPointerIndex = utils.clamp(0, this.recentPointerIndex - 1, this.recentlySelected.length);
        const recent = this.recentlySelected[this.recentPointerIndex];
        if (recent) {
            const target = this.getByUuid(recent.uuid);
            if (target) {
                target.set('selected', true, true, false, false);
                this.trigger('select', {
                    node: target,
                    previous: [],
                });
            }
        }
    };

    // Post-order recursive
    calculateCheckboxes(indeterminateOnly = false) {
        this.eachPostOrder(this, child => {
            if (child.isFolder() && child.hasAnyVisibleChildren()) {
                // sometimes even if nodes are completely independent and we dont want checked state to be calculated
                // people want indeterminate to be calculated so they can find what is check inside subtree
                // we calculate if folder should be indeterminate only when its not checked
                if (indeterminateOnly && child.state.checked === 1) {
                    return;
                }

                const visibleChildren = child.getChildren(false, true);
                const childrenCount = visibleChildren.length;
                let checkedCount = 0;
                let indeterminateCount = 0;

                // number of checked items
                visibleChildren.forEach(child => {
                    if (child.state.checked === 1) checkedCount++;
                    else if (child.state.checked === 2) indeterminateCount++;
                });

                if (indeterminateOnly) {
                    child.state.checked = indeterminateCount + checkedCount > 0 ? 2 : 0;
                } else {
                    if (childrenCount === checkedCount) {
                        child.state.checked = 1;
                    } else if (checkedCount === 0) {
                        if (indeterminateCount > 0) {
                            child.state.checked = 2;
                        } else {
                            child.state.checked = 0;
                        }
                    } else {
                        child.state.checked = 2;
                    }
                }

                if (child.view) {
                    child.view.renderChecked();
                }
            }
        });
    }

    getAllVisibleAndEnabled(): TreeNode[] {
        const selected = [this, ...this.getChildren(true, true, true)];
        return selected.filter(c => c.state.enabled).filter(c => c.text?.toLowerCase().includes(this.filterText));
    }

    hideEmptyFoldersApply(rerender = true) {
        let predicateShouldHide = (node: TreeNode, counts) => false;

        if (this.hideEmptyFolders && typeof this.fetchContentCountsFn === 'function') {
            if (!this.contentCounts) {
                console.warn('tree content counts not yet loaded, skipping hide empty');
                return;
            }
            predicateShouldHide = (node, counts) => {
                let nodeId = node.id;
                if (node.isLink()) {
                    nodeId = node.ajaxData.modelId;
                }
                if (counts.hasOwnProperty(nodeId) && counts[nodeId] > 0) return false;
                if (this.hideEmptyFiles && node.isFileOrLink()) return true;
                return !node.hasAnyVisibleChildren();
            };
        }

        this.eachPostOrder(this, child => {
            // if countsFn is defined, hide both folders and models, otherwise just folders (because they probably have real files inside)
            if (this.hideEmptyFiles ? true : child.isFolder()) {
                child.state.hidden = child.isRoot() ? false : predicateShouldHide(child, this.contentCounts);
                // can't rerender individual hidden nodes, because they will lose ordering inside parent (they gate appended to the end)
                // and maybe several parent are also hidden, so just rerender the whole tree
            }
        });

        if (rerender && this.view) {
            this.root.view.render();
        }
    }

    /* renders counts text (number next to node name & '...') based on counts we get from server
     * these are NOT counts based on number of files inside folder but for example number of logpoints attached to node)
     */
    renderNodeCountsManual(totalCounts: { [key: string]: number }, filteredCounts?: { [key: string]: number }) {
        if (!totalCounts) {
            return;
        }
        if (!filteredCounts) {
            filteredCounts = totalCounts;
        }

        this.each(
            child => {
                const childId = child.isLink() ? child.ajaxData.modelId : child.id;
                if (child.isLink()) return;
                let filteredCount = filteredCounts[childId];
                const allCount = totalCounts[childId];

                // deep count (...)
                let deepChildHasItems = false;
                child.each(deepChild => {
                    if (totalCounts[deepChild.id]) {
                        deepChildHasItems = true;
                    }
                }, true);

                if (!$.isNumeric(filteredCount) && allCount > 0) {
                    filteredCount = 0;
                }

                let text = child.ajaxData.name;

                let filteredCountText = '';
                if (allCount > 0) {
                    if ($.isNumeric(filteredCount) && filteredCount != allCount) {
                        filteredCountText += '(' + filteredCount + '/' + allCount + ')';
                    } else {
                        filteredCountText = '(' + allCount + ')';
                    }
                }

                if ($.isNumeric(filteredCount) || deepChildHasItems) {
                    text += ` <span style="color: #B1B1B1">${filteredCountText} ${deepChildHasItems ? '(...)' : ''}</span>`;
                }

                child.setText(text);
            },
            true,
            true
        );
    }

    finishedLoading() {
        const progress = document.getElementById('progress');
        const bar = document.getElementById('bar');

        if (progress) {
            progress.style.width = 100 + '%';
            progress.style.transition = 'opacity 2s linear';
            progress.style.opacity = '0';
        }

        if (bar) {
            bar.style.animation = 'none';
        }

        this.root.set('loading', false);
    }

    toggleHideEmptyFolders = () => {
        this.hideEmptyFolders = !this.hideEmptyFolders;
        this.hideEmptyFoldersApply();
        this.persistState();
    };

    filterByNodeText(text: string) {
        // when starting to filter we save which nodes are expanded, so we can revert expanded state when filter is cleared (as requested by customers)
        // be careful not to animate expanding/collapsing when filtering, it can be slow and crash the browser

        if (this.filterText?.length === 0 && text?.length > 0) {
            const expandedIds = [];
            this.each(c => {
                if (c.isFolder() && c.state.expanded) {
                    expandedIds.push(c.id);
                }
            }, true);
            this.expandedIdsBeforeFilter = expandedIds;
        } else if (text.length === 0) {
            this.each(c => {
                if (c.isFolder()) {
                    c.setExpanded(this.expandedIdsBeforeFilter.includes(c.id), false);
                }
            }, true);
            this.expandedIdsBeforeFilter = [];
        }

        this.filterText = text.toLowerCase();

        this.eachPostOrder(this, child => {
            if (child.state.hidden) {
                return;
            }

            const matches = this.matchesFilterFn(child, this.filterText);

            if (matches && !child.hasAnyVisibleChildren(false)) {
                child.state.filtered = true;
            } else {
                child.state.filtered = false;
                if (text.length > 0) {
                    child.eachParent(p => p.setExpanded(true, false));
                }
            }

            if (child.view) {
                child.view.renderFiltered();
            }
        });

        const result = this.getAllVisibleAndEnabled();
        if (this.filterText?.length && result?.length) {
            result[0].setHighlighted(true, false);
        } else {
            this.each(c => c.setHighlighted(false, false), true, true);
        }
    }

    highlightNext() {
        const result = this.getAllVisibleAndEnabled();
        if (result?.length > 1) {
            const highlightedIndex = result.findIndex(c => c.state.highlighted === true);
            if (result[highlightedIndex + 1]) {
                result[highlightedIndex + 1].setHighlighted(true, true);
            } else {
                result[0].setHighlighted(true, true);
            }
        }
    }

    highlightPrevious() {
        const result = this.getAllVisibleAndEnabled();
        if (result?.length > 1) {
            const highlightedIndex = result.findIndex(c => c.state.highlighted === true);
            if (result[highlightedIndex - 1]) {
                result[highlightedIndex - 1].setHighlighted(true, true);
            } else {
                result[result.length - 1].setHighlighted(true, true);
            }
        }
    }

    selectHighlighted() {
        const result = this.getAllVisibleAndEnabled();
        const highlighted = result.find(c => c.state.highlighted === true);
        if (highlighted) {
            highlighted.setSelected(true);
            if (this.showCheckboxes && !this.selectionCheckbox && this.checkOnHighlightSelected) {
                if (highlighted.state.checked !== 1) {
                    highlighted.setChecked(1);
                }
            }
        }
    }

    unHighlightHighlighted() {
        const result = this.getAllVisibleAndEnabled();
        const highlighted = result.find(c => c.state.highlighted === true);
        if (highlighted) {
            highlighted.setHighlighted(false, false);
        }
    }

    openCreateFolder = (model: TreeNode) => {
        CreateEditFolderDialog.create(model.id, this.ajaxData, (folder: IFolder, parentId: number) => {
            // add it to source ajax data so it appears is 'new folder' dialog without page refresh
            const folderObj: IFolder = FileTreeService.findNodeById(this.ajaxData, model.id, true, false);
            // folder_tree.json returns folders in 'children' instead of 'folders' like tree_data.json
            if (Array.isArray(folderObj.folders)) {
                folderObj.folders.push({
                    id: folder.id,
                    uuid: folder.uuid,
                    name: folder.name,
                    folders: [],
                    tolerance: {},
                });
            } else {
                folderObj.children.push({
                    id: folder.id,
                    uuid: folder.uuid,
                    name: folder.name,
                    children: [],
                    tolerance: {},
                });
            }

            // add node to tree
            if (folder.id && folder.name) {
                model.addChild(folder as any);

                this.trigger('folder_created', {
                    folder,
                    parentId,
                    ajaxData: this.ajaxData,
                });
            }

            FileTreeService.clearCache();
        });
    };

    fetchAccessList = async (disableNodes = false) => {
        try {
            this.view?.el.classList.add('overlay-waiting');

            const readonlyUuids: { allReadyOnly: boolean; readOnlyFolders: string[] } = await Ajax2({
                url: `${kuura.getContextPath()}/api/1/project/${kuura.getProjectUuid()}/read-only-folders`,
            });
            this.each(
                c => {
                    c.state.readOnly = readonlyUuids.allReadyOnly || readonlyUuids.readOnlyFolders.includes(c.uuid);
                    if (c.state.readOnly) {
                        c.setSecondaryIcon('iki iki-show', __('js_files.folder_is_read_only'));
                        if (disableNodes) {
                            c.setEnabled(false);
                        }
                    }
                },
                true,
                true
            );
        } catch {
        } finally {
            this.view?.el.classList.remove('overlay-waiting');
        }
    };

    getStorageKeyForProject() {
        return 'filetree-state-' + this.storageKey + '-' + (this.storageIsGlobal ? '' : kuura.getProjectId());
    }

    // not doing this in view since its used while tree is preloading and view doesn't exists yet at that point
    progressIndeterminateShow() {
        if (!this.rootEl) return;
        this.rootEl
            .parent()
            .prepend(
                '<div class="p-progressbar p-component p-progressbar-indeterminate" style="height:6px; flex-shrink: 0; margin-bottom: 5px;" role="progressbar" aria-valuemin="0" aria-valuemax="100" data-pc-name="progressbar" data-pc-section="root"><div class="p-progressbar-indeterminate-container" data-pc-section="container"><div class="p-progressbar-value p-progressbar-value-animate" data-pc-section="value"></div></div></div>'
            );
    }

    progressIndeterminateHide() {
        if (!this.rootEl) return;
        this.rootEl.parent().find('.p-progressbar').remove();
    }

    setExpandOnStart = (state: TreeExpandOnStart) => {
        this.expandOnStart = state;
        this.persistState();
    };

    persistState = _.debounce(() => {
        if (!this.storageKey) return;

        // global state
        LStorage.setItem('filetree-state-' + this.storageKey, {
            expandOnStart: this.expandOnStart,
            hideEmptyFolders: this.hideEmptyFolders,
        });

        // per project state
        const projectKey = this.getStorageKeyForProject();
        if (this.expandOnStart === 'restoreState') {
            const expandedIds = [];
            this.each(c => {
                if (c.isFolder() && c.state.expanded) {
                    expandedIds.push(c.id);
                }
            }, true);
            LStorage.setItem(projectKey, {
                expandedIds,
            });
        } else {
            LStorage.removeItem(projectKey);
        }
    }, 100);

    getStoredState = (): { expandOnStart?: TreeExpandOnStart; expandedIds?: number[]; hideEmptyFolders?: boolean } => {
        const global = LStorage.getOrDefault('filetree-state-' + this.storageKey, {}); // global state for all projects
        const project = LStorage.getOrDefault(this.getStorageKeyForProject(), {}); // per project state, eg expanded nodes

        return {
            ...global,
            ...project,
        };
    };

    syncAllLinksWithTargets = _.debounce(() => {
        this.each(link => {
            if (link.isLink()) {
                const targetNode = this.getByUuid(link.ajaxData.modelUuid);
                if (targetNode) {
                    link.set('checked', targetNode.state.checked);
                }
            }
        }, true);

        if (this.propagateCheckbox) {
            this.calculateCheckboxes();
        }
    }, 100);

    getRealFileCounts = () => {
        const folderFileCounts = {};
        this.eachPostOrder(this, child => {
            if (child.isFolder()) {
                folderFileCounts[child.id] = child.getChildren(false, false).filter(c => c.isFileOrLink()).length;
            }
        });
        return folderFileCounts;
    };

    // EVENTS
    on = (event: TreeEvent, callback: (eventObject: JQueryEventObject, ...args: any[]) => any): TreeNode => {
        if (!this.rootEl) return;
        this.rootEl.on(event as any, callback);
        return this;
    };

    off = (event: TreeEvent, callback: (eventObject: JQueryEventObject, ...args: any[]) => any): TreeNode => {
        if (!this.rootEl) return;
        this.rootEl.off(event as any, callback);
        return this;
    };

    offAll = (): TreeNode => {
        if (!this.rootEl) return;
        this.rootEl.off();
        return this;
    };

    trigger = (event: TreeEvent, data?: any): TreeNode => {
        if (!this.rootEl) return;
        this.rootEl.trigger(event as string, data);
        return this;
    };

    getByUuid(uuid: string): TreeNode {
        const cachedNode = this.uuidCache.get(uuid);
        if (cachedNode) {
            return cachedNode;
        }

        const result: TreeNode = super.getByUuid(uuid);
        if (result) {
            this.uuidCache.set(uuid, result);
            return result;
        }
        return null;
    }

    getById(id: number): TreeNode {
        const cachedNode = this.idCache.get(id);
        if (cachedNode) {
            return cachedNode;
        }

        const result: TreeNode = super.getById(id);
        if (result) {
            this.idCache.set(id, result);
            return result;
        }
        return null;
    }

    addChild(childData: ITreeData): TreeNode {
        const child = super.addChild(childData);
        if (child) {
            if (child.uuid) {
                this.uuidCache.set(child.uuid, child);
            }
            if (child.id) {
                this.idCache.set(child.id, child);
            }
        }
        return child;
    }

    cachePut(node: TreeNode) {
        if (node.uuid) {
            this.uuidCache.set(node.uuid, node);
        }
        if (node.id) {
            this.idCache.set(node.id, node);
        }
    }

    cacheSize() {
        return this.uuidCache.size;
    }

    focusFilter() {
        setTimeout(() => {
            if (this.view) {
                const el = this.view.parentUL.parentNode.parentNode.querySelector('.file-tree__toolbar__text-filter') as HTMLInputElement;
                el?.focus();
            }
        }, 100);
    }
}

export class TreeView {
    static renderers: any = {
        selected: 'renderSelected',
        expanded: 'renderExpanded',
        checked: 'renderChecked',
        loading: 'renderLoading',
        enabled: 'renderEnabled',
        highlighted: 'renderHighlighted',
        secondaryIcon: 'renderSecondaryIcon',
    };
    el: HTMLLIElement;
    ul: HTMLUListElement;
    labelEl: HTMLDivElement;
    input: HTMLInputElement;
    parentUL: HTMLUListElement;
    textSpan: HTMLSpanElement;
    public model: TreeNode;

    constructor(model: TreeNode) {
        this.model = model;
        if (!(this.model instanceof TreeNode)) {
            throw new Error("FileTree - Can't make tree view without a model");
        }
    }

    destroy() {
        if (this.model.isRoot()) {
            (this.model as Tree).rootEl[0].replaceChildren();
        }
        if (this.toolbarEl) {
            this.toolbarEl.remove();
            this.toolbarEl = null;
        }
        if (this.selectionFeedbackEl) {
            this.selectionFeedbackEl.remove();
            this.selectionFeedbackEl = null;
        }
        if (this.el) {
            this.el.remove();
            this.el = null;
        }
        this.model = null;
    }

    scrollTo = () => {
        setTimeout(() => {
            this.el.scrollIntoView({
                behavior: 'auto',
                block: 'nearest',
                inline: 'nearest',
            });
        }, 150);
    };

    render() {
        if (!this.model.root.renderView || this.model.state.hidden) return;

        this.el = document.createElement('li');
        this.el.className = `filetree ${this.model.state.expanded ? 'expanded' : ''}`;
        this.el.dataset.uuid = String(this.model.uuid);
        this.el.dataset.type = String(this.model.type);

        // ensure there is a parent <ul> to append to
        if (this.model.isRoot()) {
            this.parentUL = document.createElement('ul');
            this.parentUL.className = 'filetree-list filetree-root animated';
            if (this.model.root.showCheckboxes) {
                this.parentUL.classList.add('has-checkboxes');
            }
            if (!this.model.root.showRoot) {
                this.parentUL.classList.add('no-root');
            }
        } else {
            // parent might be missing ul because it didn't have any children at time of creation
            if (!this.model.parent.view.ul) {
                this.model.parent.view.makeUl();
            }
            this.parentUL = this.model.parent.view.ul;
        }

        // render me
        this.el.classList.add(TreeType[this.model.type].toLowerCase());
        if (this.model.type !== TreeType.DIRECTORY) {
            const safeExt = this.model.ext.toLowerCase().replace(/[^a-z0-9]/g, '_');
            this.el.classList.add('ext_' + safeExt);
        }

        const checkboxTitle = this.model.isFolder() && this.model.root.propagateCheckbox !== 'deep' ? __('js_files.tree_check_all_description') : '';
        let labelTitle = '';
        if (this.model.ajaxData?.hasOwnProperty('version') && this.model.ajaxData?.hasOwnProperty('layerCounts')) {
            labelTitle = `${__('js_files.version')}: ${
                this.model.ajaxData.version === this.model.ajaxData.latestVersion
                    ? this.model.ajaxData.version + 1
                    : this.model.ajaxData.version + 1 + '/' + this.model.ajaxData.latestVersion + 1
            }
${__('js_files.file_size')}: ${utils.bytesToLargestUnit(this.model.ajaxData.filesize)}
${__('general.creation_time')}: ${this.model.ajaxData.timestamp ? DateUtils.formatDateTime(this.model.ajaxData.timestamp) : ''}
${__('js_files.description')}: ${this.model.ajaxData.description ?? ''}
${__('js_files.file_contents')}: ${kuuraModel.getLayerCountsText(this.model.ajaxData.layerCounts)}`;
        }

        const labelEl = document.createElement('div');
        labelEl.className = 'filetree-label';
        if (!!labelTitle) {
            labelEl.title = labelTitle;
        }
        labelEl.dataset.uuid = String(this.model.uuid);
        labelEl.dataset.type = String(this.model.type);

        if (this.model.root.showCheckboxes) {
            this.input = document.createElement('input');
            this.input.type = 'checkbox';
            this.input.title = checkboxTitle;
            labelEl.appendChild(this.input);
        }

        const textSpan = document.createElement('span');
        textSpan.className = 'js-text';
        this.textSpan = textSpan;
        this.renderText();
        labelEl.appendChild(textSpan);

        if (this.model.isRoot() && !this.model.root.showRoot) {
            labelEl.style.display = 'none';
        }

        // if folder, make <ul> for children and expand handle
        if (this.model.isFolder()) {
            this.makeUl(labelEl);
        }

        // render children
        for (const childModel of this.model.children) {
            if (childModel.view) {
                childModel.view.render();
            }
        }

        this.parentUL.appendChild(this.el);
        this.el.appendChild(labelEl);
        this.labelEl = labelEl;

        this.renderState();

        if (this.ul) {
            this.el.appendChild(this.ul);
        }

        if (this.model.isRoot()) {
            (this.model as Tree).rootEl[0].replaceChildren(this.parentUL);
            setTimeout(() => {
                this.renderToolbar();
            }, 10);
            this.model.isRendered = true;
        }

        this.bindEvents();
    }

    makeUl = (labelDiv?: HTMLElement) => {
        this.ul = document.createElement('ul');
        this.ul.className = 'filetree-list';
        this.ul.style.display = 'none';
        this.el.appendChild(this.ul);

        if (this.model.hasAnyVisibleChildren()) {
            this.el.classList.add('has-children');
        }

        const expandSpan = document.createElement('span');
        expandSpan.className = 'filetree-label-expand';
        (labelDiv || this.el.querySelector('.filetree-label')).prepend(expandSpan);
    };

    // RENDER STATE
    renderState(prop?: TreeStateOption) {
        if (!this.model.root.renderView || !this.el) return;

        if (!prop) {
            // no prop, render everything
            this[TreeView.renderers.selected]();
            this[TreeView.renderers.expanded]();
            this[TreeView.renderers.checked]();
            this[TreeView.renderers.enabled]();
            this[TreeView.renderers.secondaryIcon]();
        } else if (TreeView.renderers[prop]) {
            this[TreeView.renderers[prop]]();
        }
    }

    renderSelected() {
        if (!this.model.root.renderView || !this.el) return;
        const isSelected = this.model.get('selected');
        this.labelEl.classList.toggle('selected', isSelected);

        if (isSelected) {
            if (this.el?.closest('.filetree-root')) {
                this.el
                    .closest('.filetree-root')
                    .querySelectorAll('.directory')
                    .forEach(el => el.classList.remove('parent-of-selected'));
                this.el.closest('.directory').classList.add('parent-of-selected');
            }
        }
    }

    renderExpanded(animate = true) {
        if (!this.model.root.renderView || !this.el) return;
        if (this.model.isFolder() && this.model.hasChildren()) {
            const expanded = this.model.get('expanded');
            this.el.classList.toggle('expanded', expanded);

            if (expanded) {
                if (animate) {
                    slideDown(this.ul, 100);
                } else {
                    this.ul.style.display = 'block';
                }
            } else {
                if (animate) {
                    slideUp(this.ul, 100);
                } else {
                    this.ul.style.display = 'none';
                }
            }
        }
    }

    renderChecked() {
        if (!this.model.root.renderView || !this.el) return;
        if (this.model.root.showCheckboxes) {
            const state = this.model.get('checked');
            this.el.classList.toggle('checked', state == 1);

            this.input.indeterminate = false;
            if (state === 0) {
                this.input.checked = false;
            } else if (state === 1) {
                this.input.checked = true;
            } else if (state === 2) {
                this.input.indeterminate = true;
            }
        }
    }

    renderEnabled() {
        if (!this.model.root.renderView || !this.el) return;
        this.labelEl.classList.toggle('disabled', !this.model.state.enabled);
    }

    renderHighlighted() {
        if (!this.model.root.renderView || !this.el) return;
        this.labelEl.classList.toggle('highlighted', this.model.state.highlighted);
    }

    renderLoading() {
        if (!this.model.root.renderView || !this.el) return;
        this.el.classList.toggle('loading', this.model.state.loading);
    }

    renderFiltered() {
        if (!this.model.root.renderView || !this.el) return;
        this.el.style.display = this.model.state.filtered ? 'none' : 'block';
    }

    renderText() {
        if (!this.model.root.renderView || !this.el) return;
        this.textSpan.innerHTML = this.model.text;
    }

    renderError() {
        if (!this.model.root.renderView || !this.el) return;
        this.labelEl.classList.toggle('has-error', !!this.model.axiosState.error);
        this.labelEl.title = typeof this.model.axiosState.error === 'string' ? this.model.axiosState.error : '';
    }

    renderSecondaryIcon() {
        if (!this.model.root.renderView || !this.el) return;
        if (this.model.secondaryIcon) {
            setTimeout(() => {
                // timeout because wrapper doesn't exist on initial load
                this.labelEl.querySelector('.file_icon_secondary')?.remove();

                const iconElement = document.createElement('i');
                iconElement.className = `file_icon_secondary ${this.model.secondaryIcon}`;
                if (this.model.secondaryIconTooltip) {
                    iconElement.title = this.model.secondaryIconTooltip;
                }
                this.labelEl.appendChild(iconElement);
            });
        } else {
            this.labelEl.querySelector('.file_icon_secondary')?.remove();
        }
    }

    toolbarEl: HTMLDivElement = null;
    selectionFeedbackEl: HTMLDivElement = null;

    renderToolbar() {
        if (this.model?.isRoot() && this.model.root.toolbar?.show) {
            if (!this.toolbarEl) {
                const parent = this.parentUL.parentNode as HTMLDivElement;
                if (!parent) {
                    return;
                }

                if (this.model.root.toolbar.elementSelector) {
                    this.toolbarEl = parent.parentNode.querySelector(this.model.root.toolbar.elementSelector);
                } else {
                    this.toolbarEl = document.createElement('div');
                    this.toolbarEl.className = 'tree-toolbar-wrapper';
                    parent.insertAdjacentElement('beforebegin', this.toolbarEl);
                }

                this.selectionFeedbackEl = document.createElement('div');
                this.selectionFeedbackEl.className = 'tree-selection-feedback-wrapper';
                parent.insertAdjacentElement('beforebegin', this.selectionFeedbackEl);
            }

            ReactUtils.safeRender(
                React.createElement(TreeToolbar, {
                    tree: this.model.root,
                    ...this.model.root.toolbar,
                }),
                this.toolbarEl
            );
        }
    }

    private bindEvents() {
        // for perf, only bind on root and delegate
        if (this.model && this.model.isRoot()) {
            const longTouchHandler = new LongTouchHandler(
                e => {
                    // regular click, short tap
                    const view: TreeView = this.getViewForUuid((e.target as HTMLElement).closest('li').dataset.uuid);
                    view.clickItemHandler(e, view);
                },
                e => {
                    // long press
                    const view: TreeView = this.getViewForUuid((e.target as HTMLElement).closest('li').dataset.uuid);
                    if ((e.target as HTMLElement).nodeName === 'INPUT') {
                        view.model.toggleCheckedAndForcePropagate();
                    }
                }
            );

            // must block click event so pointerup/down will work as expected (with long press)
            this.el.addEventListener('click', e => {
                e.stopPropagation();
                e.preventDefault();
            });

            this.el.addEventListener('dblclick', e => {
                this.model.root.trigger('dblclick', this.model);
            });

            this.el.addEventListener('pointerdown', (e: PointerEvent) => {
                // we need to do stopPropagation, but that will prevent existing context menu from closing so do it manually
                const existing = document.querySelector('[ik-context-menu-holder]') as HTMLDivElement;
                if (existing) {
                    try {
                        ReactUtils.safeUnmountComponentAtNode(existing);
                        existing.remove();
                    } catch (e) {}
                }

                e.stopPropagation();
                e.preventDefault();
                longTouchHandler.onTouchStart(e);
            });

            this.el.addEventListener('pointerup', e => {
                e.stopPropagation();
                e.preventDefault();
                longTouchHandler.onTouchEnd(e);
            });

            if (this.model.root.showContextMenu) {
                this.el.addEventListener('contextmenu', e => {
                    e.preventDefault();
                    if ((e.target as HTMLElement).nodeName === 'INPUT') return;

                    const view: TreeView = this.getViewForUuid($(e.target).closest('li').data('uuid'));

                    const ctxModel: IContextMenuItem[] = [];
                    const isMobilePage = window.location.href.includes('mobile');
                    if (typeof view.model.root.onContextMenu === 'function') {
                        const customModel: IContextMenuItem[] = view.model.root.onContextMenu(view.model);
                        ctxModel.push(...(customModel ?? []));
                    }

                    if (view.model.isFileOrLink()) {
                        const modelId = view.model.isLink() ? view.model.ajaxData.modelId : view.model.ajaxData.id;
                        const modelUuid = view.model.isLink() ? view.model.ajaxData.modelUuid : view.model.ajaxData.uuid;
                        const folderId = view.model.isLink() ? view.model.ajaxData.model.folderId : view.model.ajaxData.folderId;

                        if (kuura.hasPrivilege('Download_Model')) {
                            ctxModel.push({
                                label: __('general.download_file'),
                                icon: 'iki iki-download',
                                action: () => {
                                    window.open(location.origin + kuura.getContextPath() + `/api/1/file/${modelId}/get`, '_blank').focus();
                                },
                            });
                        }
                        if (!window.location.href.includes('models.htm') && !isMobilePage && kuura.hasPrivilege('MODEL_PAGE')) {
                            ctxModel.push({
                                label: __('js_menu.open_in_files_page'),
                                icon: 'iki iki-external',
                                action: () => {
                                    window.open(location.origin + kuura.getContextPath() + '/models.htm#/' + folderId + '/' + modelId, '_blank').focus();
                                },
                            });
                        }
                        if (this.model.root.showContextMenuInfo) {
                            ctxModel.push({
                                label: __('general.show_details'),
                                icon: 'iki iki-info',
                                action: () => FileInfoDialog.open(modelUuid, true, true),
                            });
                        }

                        const layerCounts = view.model.ajaxData.layerCounts;
                        if (view.model.isChecked() && layerCounts.mapLayers > 0 && layerCounts.features > 0) {
                            ctxModel.push({
                                label: __('js_map.show_model_data'),
                                action: () => {
                                    const rootTree: Tree | null = view.model.root as Tree;
                                    if (!rootTree) return;

                                    if (rootTree.featuresLoadedMap.has(modelUuid)) {
                                        rootTree.featuresLoadedMap.delete(modelUuid);
                                        Events.trigger('unload_3d_content_' + view.model.uuid, {
                                            nodeToBeLoaded: view.model,
                                        });
                                    } else {
                                        rootTree.featuresLoadedMap.set(modelUuid, true);
                                        Events.trigger('load_3d_content_' + view.model.uuid, {
                                            nodeToBeLoaded: view.model,
                                        });
                                    }
                                },
                                checked: view.model.root?.featuresLoadedMap.has(modelUuid) || false,
                            });
                        }
                    } else {
                        if (this.model.root.showContextMenuCreateFolder && !isMobilePage && !view.model.state.readOnly) {
                            ctxModel.push({
                                label: __('js_files.create_folder'),
                                icon: 'iki iki-folder-closed',
                                action: () => this.model.root.openCreateFolder(view.model),
                            });
                        }
                        if (this.model.root.showContextMenuInfo) {
                            ctxModel.push({
                                label: __('general.show_details'),
                                icon: 'iki iki-info',
                                action: () => CreateEditFolderDialog.info(view.model),
                            });
                        }
                    }

                    if (!ctxModel?.length) return;

                    Events.trigger('map-hide-overlays');
                    view.model.root.view.el.querySelectorAll('.context-highlight').forEach(el => el.classList.remove('context-highlight'));
                    setTimeout(() => this.el.querySelector('.filetree-label').classList.add('context-highlight'), 100); // gets cleared if done too fast
                    renderContextMenuAtPosition(ctxModel, e.pageX, e.pageY, 'tree-context-menu', () => {
                        view.model.root.view.el.querySelectorAll('.context-highlight').forEach(el => el.classList.remove('context-highlight'));
                    });
                });
            }
        }
    }

    public clickItemHandler(e: Event, view: TreeView) {
        // clicked expand handle
        if ((e.target as HTMLElement).classList.contains('filetree-label-expand')) {
            const expanded = view.model.state.expanded;

            // expand-collapse all if shift is down
            if ((e as MouseEvent).shiftKey) {
                view.model.each(tree => tree.setExpanded(!expanded), true, true);
            } else {
                view.model.setExpanded(!expanded, true);
            }
        }

        // clicked checkbox
        else if ((e.target as HTMLElement).nodeName === 'INPUT') {
            setTimeout(() => {
                if ((e as MouseEvent).shiftKey) {
                    view.model.toggleCheckedAndForcePropagate();
                } else {
                    if (view.model.get('checked') === 2) {
                        view.model.setChecked(1);
                    } else {
                        view.model.setChecked(view.model.get('checked') ? 0 : 1);
                    }
                    if (this.model.root.propagateCheckbox) {
                        view.model.root.calculateCheckboxes();
                    }
                }
            }, 0);
        }

        // clicked label
        else {
            view.model.setSelected(true);
        }
    }

    getViewForUuid(uuidOrId: any): TreeView | null {
        return this.model.root.getByUuid(uuidOrId)?.view ?? this.model.root.getById(uuidOrId)?.view ?? null;
    }
}

// we need long press function on checkboxes
// usually that would be done by hijacking contextmenu event which mobile devices fire on long press
// but that doesn't work in safari, so all this is a workaround for ios

class LongTouchHandler {
    private currentTimeout: any = null;
    private currentEvent?: PointerEvent;
    private downTimestamp = null;
    private startX = 0;
    private startY = 0;

    constructor(private onTap: (e: any) => void, private onLongTouch: (e: any) => void) {}

    onTouchStart(e: PointerEvent) {
        this.currentEvent = e;
        this.downTimestamp = Date.now();
        this.currentTimeout = setTimeout(() => {
            if (this.currentEvent) {
                this.onLongTouch(this.currentEvent);
            }
        }, 300);

        this.startX = e.clientX;
        this.startY = e.clientY;
        window.document.addEventListener('touchmove', this.onTouchMove);
    }

    onTouchEnd(e: PointerEvent) {
        window.document.removeEventListener('touchmove', this.onTouchMove);
        if (Date.now() - this.downTimestamp < 299) {
            this.onTap(this.currentEvent);
        }

        clearTimeout(this.currentTimeout);
        this.currentEvent = null;
        this.downTimestamp = null;
    }

    onTouchMove = (e: TouchEvent) => {
        const tap = e.changedTouches[0];
        if (!this.startY || !this.startX) {
            this.startX = tap.clientX;
            this.startY = tap.clientY;
        }

        if (Math.abs(this.startX - tap.clientX) > 3 || Math.abs(this.startY - tap.clientY) > 3) {
            window.document.removeEventListener('touchmove', this.onTouchMove);
            clearTimeout(this.currentTimeout);
            this.currentEvent = null;
            this.downTimestamp = null;
        }
    };
}

const slideDown = (element: HTMLElement, duration = 100) => {
    if (element.offsetHeight > 0) {
        return Promise.resolve();
    }

    element.style.display = 'block';
    const height = element.scrollHeight;
    element.style.height = '0px';
    element.style.overflow = 'hidden';

    return element
        .animate([{ height: '0px' }, { height: `${height}px` }], {
            duration,
            easing: 'ease-out',
        })
        .finished.then(() => {
            element.style.height = '';
            element.style.overflow = '';
        })
        .catch(error => {
            console.log('Animation error:', error);
        });
};

const slideUp = (element: HTMLElement, duration = 100) => {
    if (element.offsetHeight === 0) {
        element.style.display = 'none';
        return Promise.resolve();
    }

    const height = element.scrollHeight;
    element.style.height = `${height}px`;
    element.style.overflow = 'hidden';

    return element
        .animate([{ height: `${height}px` }, { height: '0px' }], {
            duration,
            easing: 'ease-out',
        })
        .finished.then(() => {
            element.style.display = 'none';
            element.style.height = '';
            element.style.overflow = '';
        })
        .catch(error => {
            console.log('Animation error:', error);
        });
};
